import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import Swal, { SweetAlertCustomClass } from 'sweetalert2';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { SocketIoService } from '@core/services/socket.io-service';
import { svgIconList } from '@shared/svg-icon-list';
import { getMemberAccountId } from '@shared/utils/member-account-id.util';
import { TwoStepDepositService } from './two-step-deposit.service';
import { TwoStepDepositDataService } from './two-step-deposit-data.service';

declare var $: any;

@Injectable({
  providedIn: 'root'
})
export class TransactionStatusUpdateService {

  svg: any = svgIconList;
  sitePrefix = environment.sitePrefix.toLocaleLowerCase();
  customClass: SweetAlertCustomClass = {
    htmlContainer: 'customPopupAnnouncement'
  };
  unsubscribeTransactionStatus: (() => void) | null = null;
  transactionStatusChannelName: string | null = null;
  constructor(
    private twoStepDepositService: TwoStepDepositService,
    private twoStepDepositDataService: TwoStepDepositDataService,
    private eventEmitterService: EventEmitterService,
    private socketIoService: SocketIoService,
  ) {}

  /**
   * AIO-82318: the member's own transaction-status channel. api2 broadcasts
   * UpdateTransactionStatusEvent on `transaction.status{member_account_id}` whenever a deposit or
   * withdrawal is approved or rejected in the backoffice (DepositObserver / WithdrawObserver),
   * and this puts the backend's message in front of the member.
   *
   * The whole body arrived commented out in the initial qply scaffolding commit (7c704063) and
   * was never switched on, so until now a backoffice approval produced no reaction at all.
   * Restored from template-02 desktop core/services/transaction-status-update.service.
   *
   * Deviations, each forced by qply rather than chosen:
   *   - the member id comes from `getMemberAccountId()`. The original does
   *     `JSON.parse(localStorage.getItem('user_data')).id`, which throws on a normal qply login —
   *     that writes `memberProfile`, not `user_data` — and would take the whole subscription down.
   *   - template-02 also fires `clarityService.depositCompleted()`, an `onDepositGtagEmitter`
   *     event and Facebook `fbq` conversions here. qply's ClarityService has no
   *     depositCompleted, EventEmitterService has no onDepositGtagEmitter, and the fbq calls are
   *     gated to the `tvb` / `qp11` brands, so none of it applies to qp2a-qp2d. Left out rather
   *     than stubbed; port them with the analytics work if those brands ever land here.
   */
  listenTransactionUpdateEvent()
  {
    const memberAccountId = getMemberAccountId();

    if (!memberAccountId) {
      return;
    }

    this.transactionStatusChannelName = 'transaction.status' + memberAccountId;
    this.unsubscribeTransactionStatus = this.socketIoService.private(this.transactionStatusChannelName).listen('UpdateTransactionStatusEvent', (data: any) => {
      let iconDisplay = null;

      if (data.status == 1) {
        iconDisplay = this.svg.successIcon;
      } else if (data.status == 5) {
        iconDisplay = this.svg.alertIcon;
      } else {
        iconDisplay = this.svg.failedIcon;
      }

      Swal.fire({
        title: '<div>' + iconDisplay + '</div>',
        html: '<div class="text-center font-color-6">' + data.message + '</div>',
        showDenyButton: false,
        showCloseButton: true,
        showCancelButton: false,
        showConfirmButton: true,
        reverseButtons: true,
        confirmButtonText: 'OK',
        customClass: {
          confirmButton: 'confirm-button',
        }
      }).then(() => {
        const url = window.location.href;
        const stepTwoDeposit = this.twoStepDepositDataService.getLSStepTwoDeposit();

        /* As in template-02: this clears the LEGACY two-step session only (`stepTwoDeposit`).
           The new flow's `TwostepDeposit` key is untouched there too, so a new-flow member left
           on step 2 still has to cancel or let the countdown expire. */
        if (stepTwoDeposit && url.includes('member/deposit')) {
          if (data.status == 1) { // deposit approved
            this.twoStepDepositService.stepTwoDepositPaid();
          } else if (data.status == 2) { // deposit rejected
            this.twoStepDepositDataService.clearStepTwoLocalStorageAndReload();
          }
        }
      });
    });
  }

  disconnectSocket()
  {
    if(this.unsubscribeTransactionStatus){
      this.unsubscribeTransactionStatus();
      if (this.transactionStatusChannelName) {
        // this.socketIoService.leave(this.transactionStatusChannelName);
      }
    }
    this.unsubscribeTransactionStatus = null;
    this.transactionStatusChannelName = null;
  }
}
